import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ApiService from '../services/apiService';
import { downloadBlob } from '../utils/downloadFile';
import { getTranscriptSpeakerPalette } from '../utils/transcriptSpeaker';
import { logger } from '../utils/logger';

interface TranscriptLine {
    speaker: string;
    text: string;
    startTime: number;
    endTime: number;
}

interface TranscriptViewerProps {
    transcript: string;
    fileId?: string;
    currentTime?: number;
    onSeek?: (time: number) => void;
    canDownload?: boolean;
}

const TranscriptViewer: React.FC<TranscriptViewerProps> = ({
    transcript,
    fileId,
    currentTime = 0,
    onSeek,
    canDownload = true
}) => {
    const { t } = useTranslation();
    const searchResultRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);
    const [isDownloading, setIsDownloading] = useState(false);
    // Downloadfout inline in het paneel tonen (geen browser-alert), wegklikbaar.
    const [downloadError, setDownloadError] = useState<string | null>(null);
    const apiService = new ApiService();

    // Parse transcript lines met timestamps
    const parseTranscriptLines = (text: string): TranscriptLine[] => {
        const lines = text.split('\n').filter(line => line.trim());
        const parsedLines: TranscriptLine[] = [];

        lines.forEach(line => {
            // Match formaat: "Speaker: Text [startTime-endTime]"
            const match = line.match(/^(.+?):\s*(.+?)\s*\[(\d+\.?\d*)-(\d+\.?\d*)\]$/);
            if (match) {
                const [, speaker, text, startTime, endTime] = match;
                parsedLines.push({
                    speaker: speaker.trim(),
                    text: text.trim(),
                    startTime: parseFloat(startTime),
                    endTime: parseFloat(endTime)
                });
            } else {
                // Fallback voor lijnen zonder timestamp formaat
                const speakerMatch = line.match(/^(.+?):\s*(.+)$/);
                if (speakerMatch) {
                    const [, speaker, text] = speakerMatch;
                    parsedLines.push({
                        speaker: speaker.trim(),
                        text: text.trim(),
                        startTime: 0,
                        endTime: 0
                    });
                }
            }
        });

        return parsedLines;
    };

    const transcriptLines = useMemo(() => parseTranscriptLines(transcript), [transcript]);

    // Zoekfunctionaliteit
    const searchResults = useMemo(() => {
        if (!searchTerm.trim()) {
            return [];
        }

        const results: number[] = [];
        const lowerSearchTerm = searchTerm.toLowerCase().trim();

        transcriptLines.forEach((line, index) => {
            // Zoek in zowel tekst als spreker
            const textMatch = line.text.toLowerCase().includes(lowerSearchTerm);
            const speakerMatch = line.speaker.toLowerCase().includes(lowerSearchTerm);

            if (textMatch || speakerMatch) {
                results.push(index);
            }
        });

        logger.log(`Zoekterm: "${searchTerm}", Aantal resultaten: ${results.length}`, results);
        return results;
    }, [searchTerm, transcriptLines]);

    // Update current search index when search results change
    useEffect(() => {
        if (searchResults.length === 0) {
            setCurrentSearchIndex(-1);
        } else if (currentSearchIndex === -1) {
            setCurrentSearchIndex(0);
        } else if (currentSearchIndex >= searchResults.length) {
            setCurrentSearchIndex(searchResults.length - 1);
        }
        logger.log(`Search index updated: ${currentSearchIndex}, Results: ${searchResults.length}`);
    }, [currentSearchIndex, searchResults]);

    // Scroll naar zoekresultaat
    useEffect(() => {
        if (currentSearchIndex >= 0 && searchResults.length > 0) {
            const resultIndex = searchResults[currentSearchIndex];
            const ref = searchResultRefs.current[resultIndex];
            if (ref) {
                ref.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        }
    }, [currentSearchIndex, searchResults]);

    // Vind actieve lijn gebaseerd op huidige tijd
    const getActiveLineIndex = (): number => {
        if (currentTime === 0) return -1;

        return transcriptLines.findIndex(line =>
            currentTime >= line.startTime && currentTime <= line.endTime
        );
    };

    const activeLineIndex = getActiveLineIndex();

    // Scroll naar actieve lijn (alleen als er niet gezocht wordt)
    useEffect(() => {
        if (activeLineIndex >= 0 && !searchTerm.trim()) {
            const activeElement = searchResultRefs.current[activeLineIndex];
            if (activeElement) {
                activeElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        }
    }, [activeLineIndex, searchTerm]);

    const handleLineClick = (line: TranscriptLine) => {
        if (line.startTime > 0 && onSeek) {
            onSeek(line.startTime);
        }
    };

    const handlePreviousSearch = () => {
        if (searchResults.length > 0) {
            const newIndex = currentSearchIndex > 0 ? currentSearchIndex - 1 : searchResults.length - 1;
            logger.log(`Previous search: ${currentSearchIndex} -> ${newIndex}`);
            setCurrentSearchIndex(newIndex);
        }
    };

    const handleNextSearch = () => {
        if (searchResults.length > 0) {
            const newIndex = currentSearchIndex < searchResults.length - 1 ? currentSearchIndex + 1 : 0;
            logger.log(`Next search: ${currentSearchIndex} -> ${newIndex}`);
            setCurrentSearchIndex(newIndex);
        }
    };

    const handleDownloadTranscript = async () => {
        if (!fileId || !canDownload) return;

        setDownloadError(null);
        try {
            setIsDownloading(true);
            const blob = await apiService.downloadTranscript(fileId);

            await downloadBlob(blob, `transcript-${fileId}.txt`);
        } catch (error) {
            logger.error('Error downloading transcript:', error);
            setDownloadError(t('transcriptViewer.downloadError'));
        } finally {
            setIsDownloading(false);
        }
    };

    const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (event.shiftKey) {
                handlePreviousSearch();
            } else {
                handleNextSearch();
            }
        } else if (event.key === 'Escape') {
            event.preventDefault();
            setSearchTerm('');
        }
    };

    const highlightText = (text: string, searchTerm: string): React.ReactNode => {
        if (!searchTerm.trim()) return text;

        // Eenvoudigere en betrouwbaardere highlight implementatie
        const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
        const parts = text.split(regex);

        return parts.map((part, index) => {
            // Controleer of dit deel overeenkomt met de zoekterm (case insensitive)
            if (part.toLowerCase() === searchTerm.toLowerCase()) {
                return (
                    <mark key={index} className="transcript-search-hit">
                        {part}
                    </mark>
                );
            }
            return part;
        });
    };

    if (!transcript.trim()) {
        return (
            <div className="transcript-empty">
                {t('transcriptViewer.empty')}
            </div>
        );
    }

    return (
        <div className="transcript-panel">
            {/* Zoekbalk */}
            <div className="transcript-search">
                <div className="transcript-search-input-wrap">
                    <svg className="transcript-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input
                        type="text"
                        className="transcript-search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        placeholder={t('transcriptViewer.searchPlaceholder')}
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            className="transcript-search-clear"
                            onClick={() => setSearchTerm('')}
                            aria-label={t('transcriptViewer.clearSearch')}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 6L6 18M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
                {searchTerm && (
                    <div className="transcript-search-nav">
                        <span className="transcript-search-count">
                            {searchResults.length > 0 ? `${currentSearchIndex + 1} / ${searchResults.length}` : '0 / 0'}
                        </span>
                        <button
                            type="button"
                            className="transcript-search-nav-btn"
                            onClick={handlePreviousSearch}
                            disabled={searchResults.length === 0}
                            aria-label={t('transcriptViewer.previousResult')}
                            title={t('transcriptViewer.previousResultTitle')}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 15l-6-6-6 6" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            className="transcript-search-nav-btn"
                            onClick={handleNextSearch}
                            disabled={searchResults.length === 0}
                            aria-label={t('transcriptViewer.nextResult')}
                            title={t('transcriptViewer.nextResultTitle')}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                <path d="M6 9l6 6 6-6" />
                            </svg>
                        </button>
                    </div>
                )}
                {fileId && canDownload && (
                    <button
                        type="button"
                        className="transcript-search-nav-btn"
                        onClick={handleDownloadTranscript}
                        disabled={isDownloading}
                        aria-label={t('transcriptViewer.downloadTranscript')}
                        title={t('transcriptViewer.downloadTranscript')}
                    >
                        {isDownloading ? (
                            <svg className="animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        )}
                    </button>
                )}
            </div>

            {downloadError && (
                <div className="notice error" role="alert" style={{ margin: '0 0 10px' }}>
                    <p>{downloadError}</p>
                    <button type="button" onClick={() => setDownloadError(null)}>{t('common.close')}</button>
                </div>
            )}

            {/* Transcriptregels met klik-naar-tijd */}
            <div className="transcript-content">
                {transcriptLines.map((line, index) => {
                    const isActivePlayback = index === activeLineIndex;
                    const isSearchResult = searchResults.includes(index);
                    const isCurrentSearchResult = searchResults[currentSearchIndex] === index;
                    const palette = getTranscriptSpeakerPalette(line.speaker, line.speaker);

                    return (
                        <div
                            key={index}
                            ref={(el) => {
                                searchResultRefs.current[index] = el;
                            }}
                            onClick={() => handleLineClick(line)}
                            data-line-index={index}
                            className={`transcript-line${isActivePlayback ? ' active' : ''}${isSearchResult ? ' is-search-match' : ''}${isCurrentSearchResult ? ' is-current-match' : ''}`}
                        >
                            <span
                                className="transcript-speaker"
                                style={{
                                    backgroundColor: palette.surface,
                                    borderRadius: '999px',
                                    boxShadow: `inset 0 0 0 1px ${palette.accent}33`,
                                    color: palette.ink,
                                    display: 'inline-flex',
                                    padding: '0.15rem 0.5rem',
                                }}
                            >
                                {highlightText(line.speaker, searchTerm)}
                            </span>
                            <span className="transcript-text">
                                {highlightText(line.text, searchTerm)}
                            </span>
                            {line.startTime > 0 && (
                                <span className="transcript-timestamp">
                                    {formatTime(line.startTime)}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export default TranscriptViewer; 
